/**
 * Pure planner: determines which document artifacts an FD edit invalidates.
 */

const FULLY_APPROVED_STATUS = "FULLY_APPROVED";
const BUBBLE_IMPORT_HASH = "bubble import";

/**
 * Minimal shape the planner needs. Dates may be Prisma `Date`s or serialized
 * strings because the backend and web cache both use this function.
 */
export interface ClearDocumentApprovalsAndPDFsPlanInput {
  attorneyAApprovedAt: Date | string | null;
  attorneyBApprovedAt: Date | string | null;
  finalizedAt: Date | string | null;
  reviewedAt: Date | string | null;
  status: string;
  proofFileId: string | null;
  generatedPdfFileId: string | null;
  lastGeneratedInputHash: string | null;
  /**
   * True when the document has any non-soft-deleted notarization row (in
   * progress OR completed). While such a row exists the document's cached
   * PDFs and approval state are preserved — Proof needs `proofFileId` to
   * stay stable during signing, and a completed notarization is the
   * point of no return. Cleanup at cancel sites must soft-delete the
   * notarization row first so this flips back to `false`.
   */
  hasActiveOrCompletedNotarization: boolean;
}

export interface ClearDocumentApprovalsAndPDFsPlan {
  hasAttorneyApprovalsToClear: boolean;
  hasCachedPdfsToClear: boolean;
  hasReviewedAtToClear: boolean;
  wasFullyApproved: boolean;
  isPastPointOfNoReturn: boolean;
  isBubbleImport: boolean;
}

export function planClearDocumentApprovalsAndPDFs(
  input: ClearDocumentApprovalsAndPDFsPlanInput,
): ClearDocumentApprovalsAndPDFsPlan {
  const isPastPointOfNoReturn = input.hasActiveOrCompletedNotarization;
  const isBubbleImport = input.lastGeneratedInputHash === BUBBLE_IMPORT_HASH;

  const hasAttorneyApprovalsToClear =
    !isPastPointOfNoReturn &&
    (!!input.attorneyAApprovedAt || !!input.attorneyBApprovedAt);

  const canClearReviewedArtifacts = !isBubbleImport && !isPastPointOfNoReturn;
  const hasCachedPdfsToClear =
    canClearReviewedArtifacts &&
    (!!input.proofFileId || !!input.generatedPdfFileId);
  const hasReviewedAtToClear = canClearReviewedArtifacts && !!input.reviewedAt;

  const wasFullyApproved = input.status === FULLY_APPROVED_STATUS;

  return {
    hasAttorneyApprovalsToClear,
    hasCachedPdfsToClear,
    hasReviewedAtToClear,
    wasFullyApproved,
    isPastPointOfNoReturn,
    isBubbleImport,
  };
}

/** True when no document artifacts need clearing. */
export function clearDocumentApprovalsAndPDFsPlanIsNoOp(
  plan: ClearDocumentApprovalsAndPDFsPlan,
): boolean {
  return (
    !plan.hasAttorneyApprovalsToClear &&
    !plan.hasCachedPdfsToClear &&
    !plan.hasReviewedAtToClear
  );
}
